import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { isXlsxBuffer, decodeText } from '../DocumentViewer/spreadsheetBytes';
import { MIN_ROWS, MIN_COLS } from './fileTypes';
import { splitSparseTail } from './sparseTail';
import { FortuneSheet, ReadOnlyAddBand } from './types';

/**
 * Worker-side pre-check that estimates a workbook's cell count from cheap,
 * partial reads (zip central directory + a bounded XML head read) BEFORE
 * `exceljs.load` ever runs — the OOM guard for the perf package.
 * See `docs/conventions/sheet-editor.md` for the module map.
 */

/** Refusal ceiling, in FILLED cells (cells holding a value or formula): above
 *  this estimated (pre-parse) or parsed count the file is refused outright —
 *  `exceljs.load` is never called for an over-estimate. Provenance: the
 *  2026-10 measured memory budget — page + worker share ~4 GiB (survive
 *  3.77 GB / crash 4.17 GB); opening a 2M-filled-cell workbook costs ~2.6 GB. */
export const MAX_OPENABLE_CELLS = 2_000_000;

/** Above this many FILLED cells a workbook that is still openable opens
 *  read-only: re-serialising it (exceljs) would cross the tab's memory budget. */
export const MAX_EDITABLE_CELLS = 1_000_000;

/** Above this estimate (and at/under MAX_OPENABLE_CELLS) a non-blocking
 *  "Large file — may take a few seconds" notice is shown while it parses. */
export const LARGE_NOTICE_CELLS = 500_000;

/** A minimal `<c r="A1"><v>1</v></c>` cell element is 22 bytes — a
 *  conservative lower bound used to convert uncompressed sheet-XML bytes
 *  into an upper bound on cell count. */
export const XML_BYTES_PER_CELL_MIN = 20;

/** Measured bytes-per-cell ratio for legacy xls/BIFF files (not a zip, so
 *  there is no central directory to read sizes from). */
export const BIFF_BYTES_PER_CELL = 9.4;

/** A single-cell dimension (e.g. "A1") paired with sheet XML over this size is
 *  treated as degenerate — a formatting-inflated `<dimension>` rather than a
 *  real one-cell sheet — and estimated from XML bytes alone. */
const DEGENERATE_SHEET_XML_BYTES = 1_048_576; // 1 MiB

/** How much of each sheet part's XML to decode looking for `<dimension>`
 *  before giving up and treating it as missing. */
const DIMENSION_HEAD_READ_BYTES = 64 * 1024;

const DIMENSION_RE = /<dimension\s+ref="([^"]+)"/i;
// A SpreadsheetML cell element's start tag, with an optional namespace prefix
// (`<c r="A1">`, `<c/>`, `<x:c s="1">`) — never `<col>`, `<cols>` or `<cfRule>`.
const CELL_TAG_RE = /<(?:\w+:)?c[\s/>]/g;
// A tag split across two stream chunks is seen whole once this much of the
// previous chunk is prepended; covers any realistic namespace prefix.
const CELL_TAG_CARRY_CHARS = 32;
// xl/worksheets/sheet1.xml etc, but NOT xl/worksheets/_rels/sheet1.xml.rels
// — the pattern's `[^/]+\.xml$` can't match a path with another `/`
// in it, so the _rels subfolder is excluded without a separate check.
const SHEET_XML_PATTERN = /^xl\/worksheets\/[^/]+\.xml$/i;

export type SheetSizeClass = 'ok' | 'large' | 'too_large' | 'unknown';

/** What `SheetTooLargeError.cells` counts: a pre-parse upper-bound 'estimate',
 *  the parsed 'filled' cell count, or the padded 'grid' slot count. */
export type TooLargeBasis = 'estimate' | 'filled' | 'grid';

export class SheetTooLargeError extends Error {
    readonly cells: number;
    readonly basis: TooLargeBasis;

    constructor(cells: number, basis: TooLargeBasis = 'filled') {
        const max = basis === 'grid' ? MAX_OPENABLE_GRID_SLOTS : MAX_OPENABLE_CELLS;
        super(`Sheet too large to open: ${basis} ${cells} (max ${max})`);
        this.name = 'SheetTooLargeError';
        this.cells = cells;
        this.basis = basis;
    }
}

/** A read-only-band file (> MAX_EDITABLE_CELLS filled) cannot be saved or added
 *  to Workspace because only its original .xlsx bytes may be stored. */
export class SheetReadOnlyAddError extends Error {
    readonly cells: number;

    constructor(cells: number) {
        super(`Sheet is read-only: ${cells} filled cells (max editable ${MAX_EDITABLE_CELLS})`);
        this.name = 'SheetReadOnlyAddError';
        this.cells = cells;
    }
}

/** Parses an Excel dimension ref ("A1", "A1:AD200000", …) into a cell count.
 *  Reuses SheetJS's own range decoder (already a dependency of this module,
 *  loaded in the same worker/main bundle) instead of a bespoke A1 parser. */
export function parseDimensionRef(ref: string | null | undefined): { rows: number; cols: number } | null {
    if (!ref) return null;
    try {
        const { s, e } = XLSX.utils.decode_range(ref);
        if (e.r < s.r || e.c < s.c) return null;
        return { rows: e.r - s.r + 1, cols: e.c - s.c + 1 };
    } catch {
        return null;
    }
}

export interface SheetPartEstimate {
    /** The sheet's `<dimension ref="...">` value, or null if missing/unreadable. */
    dimRef: string | null;
    /** Uncompressed size (bytes) of the sheet's XML part. */
    xmlBytes: number;
    /** `<c>` cell elements counted by streaming the part — taken only when the
     *  dimension/byte bounds alone would refuse the file. */
    cellTags?: number;
}

/** The part's dimension when it can be trusted: present, and not a single cell
 *  against a large XML part (a formatting-inflated `<dimension>`, not a real
 *  one-cell sheet). */
const trustedDimension = (part: SheetPartEstimate): { rows: number; cols: number } | null => {
    const dim = parseDimensionRef(part.dimRef);
    return dim && !(dim.rows === 1 && dim.cols === 1 && part.xmlBytes > DEGENERATE_SHEET_XML_BYTES) ? dim : null;
};

/**
 * Pure combinator: per sheet, the estimate is the MIN of the
 * declared dimension's cell count and `xmlBytes / XML_BYTES_PER_CELL_MIN` —
 * both are upper bounds on the converter's real cell count, so their minimum
 * still never under-estimates. A missing dimension, or a degenerate
 * one (a single cell against a large XML part), falls back to the XML
 * bound alone rather than trusting a misleading 1-cell dimension. A streamed
 * `cellTags` count, when one was taken, replaces the dimension: it counts the
 * cells actually present, which a declared range only bounds (LibreOffice
 * declares 1,024 columns; a range can be sparsely filled or simply wrong).
 */
export function estimateFromParts(parts: SheetPartEstimate[]): number {
    return parts.reduce((total, part) => {
        const xmlBound = part.xmlBytes / XML_BYTES_PER_CELL_MIN;
        const dim = trustedDimension(part);
        const bound = part.cellTags ?? (dim ? dim.rows * dim.cols : Infinity);
        return total + Math.min(bound, xmlBound);
    }, 0);
}

const QUOTE_CHAR_CODE = 0x22; // '"'
const LF_CHAR_CODE = 0x0a; // '\n'
const CR_CHAR_CODE = 0x0d; // '\r'
const SPACE_CHAR_CODE = 0x20; // ' '

// SheetJS's own `sep=X` header line, read by `dsv_to_sheet_str` in xlsx.js 0.18.5.
const SEP_HEADER_PREFIX = 'sep=';

// guess_sep's own candidate set + tie-break weights (`guess_sep`, xlsx.js
// 0.18.5) — on a tie, including "nothing counted at all", comma wins, then
// tab, then semicolon, then pipe.
const GUESS_SEP_CHARS: Record<number, string> = { 0x2c: ',', 0x09: '\t', 0x3b: ';', 0x7c: '|' };
const GUESS_SEP_WEIGHTS: Record<number, number> = { 0x2c: 3, 0x09: 2, 0x3b: 1, 0x7c: 0 };
const GUESS_SEP_WINDOW = 1024; // dsv_to_sheet_str's own guess_sep(str.slice(0,1024)) call

/** Direct port of xlsx.js's `guess_sep` (xlsx.js 0.18.5): tallies each
 *  candidate separator over the first 1,024 characters — ignoring an
 *  occurrence while a `"` has toggled the scan "in a string" (toggled on
 *  every `"`, not just one at a field start — guess_sep's own cruder quote
 *  model, independent of the real field-splitting rule in
 *  `csvDenseGridExtent`) — then picks the highest count, breaking ties (and
 *  the "nothing counted at all" case) by GUESS_SEP_WEIGHTS. */
function guessSeparator(text: string): string {
    const window = text.slice(0, GUESS_SEP_WINDOW);
    const counts: Record<number, number> = {};
    let inQuotedRun = false;
    for (let i = 0; i < window.length; i += 1) {
        const code = window.charCodeAt(i);
        if (code === QUOTE_CHAR_CODE) inQuotedRun = !inQuotedRun;
        else if (!inQuotedRun && code in GUESS_SEP_CHARS) counts[code] = (counts[code] ?? 0) + 1;
    }
    const tally = Object.keys(counts).length ? counts : GUESS_SEP_WEIGHTS;
    // tally always has at least GUESS_SEP_WEIGHTS's 4 keys, so `.pop()` below
    // always returns one of them — never undefined.
    const winner = Object.keys(tally)
        .map(Number)
        .sort((a, b) => tally[a] - tally[b] || GUESS_SEP_WEIGHTS[a] - GUESS_SEP_WEIGHTS[b])
        .pop() as number;
    return GUESS_SEP_CHARS[winner];
}

/**
 * Resolves both the separator AND the body `dsv_to_sheet_str` (xlsx.js 0.18.5)
 * actually scans. A `sep=X` header pins `X` as the separator and is itself
 * SLICED OFF before the scan — but only when it's immediately followed by a
 * line break (`\r\n`, a lone `\r`, or a lone `\n`); otherwise SheetJS falls
 * through to `guessSeparator` on the UNSLICED text and never special-cases
 * that header at all. `X` can be any character, including one outside
 * guessSeparator's 4 fixed candidates (e.g. `sep=:`).
 */
function resolveSepAndBody(text: string): { sep: string; body: string } {
    if (text.slice(0, SEP_HEADER_PREFIX.length) === SEP_HEADER_PREFIX) {
        const sepCharPos = SEP_HEADER_PREFIX.length; // position of X in "sep=X"
        const sepChar = text.charAt(sepCharPos);
        const afterSep = text.charCodeAt(sepCharPos + 1);
        if (afterSep === CR_CHAR_CODE && text.charCodeAt(sepCharPos + 2) === LF_CHAR_CODE)
            return { sep: sepChar, body: text.slice(sepCharPos + 3) };
        if (afterSep === CR_CHAR_CODE || afterSep === LF_CHAR_CODE) return { sep: sepChar, body: text.slice(sepCharPos + 2) };
    }
    return { sep: guessSeparator(text), body: text };
}

// xlsx.js's readSync first-byte dispatch (readSync, xlsx.js 0.18.5) for the
// `type: 'string'` call converters.ts's parseCsv makes (XLSX.read(text, {
// type: 'string' })). Everything not named here falls through to its DSV/PRN
// parser (read_prn -> dsv_to_sheet_str), same as readSync's own switch.
// Every signature below is pure ASCII, and xlsx.js's own `bstrify` (run for
// `type: 'string'` input) leaves ASCII text unchanged, so comparing JS char
// codes here already matches the bytes xlsx.js would dispatch on.
const SYLK_HEADER = 'ID'; // readSync case 0x49/0x44 -> read_wb_ID
const DIF_HEADER = 'TABL'; // readSync case 0x54 + "ABL" -> DIF.to_workbook
const RTF_HEADER = '{\\rt'; // readSync case 0x7B + "\rt" -> RTF.to_workbook
const BIFF_RAW_LEAD_CHAR_CODE = 0x09; // readSync case 0x09 -> parse_xlscfb, when...
const BIFF_RAW_SECOND_CHAR_CODE_MAX = 0x08; // ...the next byte is <= this
const XML_OR_HTML_LEAD_CHAR_CODE = 0x3c; // '<' — readSync case 0x3C, and
// read_plaintext's own leading-whitespace-then-'<' check
const PLAINTEXT_SKIPPABLE_CHAR_CODES = new Set([CR_CHAR_CODE, LF_CHAR_CODE, SPACE_CHAR_CODE]);
// readSync case 0xEF: a UTF-8-encoded first character in U+F000–U+FFFF (the
// 3-byte lead byte 0xEF covers exactly that range) is a BOM-shaped lead that
// routes to parse_xlml when immediately followed by '<' (n[3]===0x3C, i.e.
// that '<' is the SECOND character — the first character's UTF-8 encoding is
// exactly 3 bytes) — otherwise it falls through to the DSV/PRN parser.
const UTF8_3BYTE_LEAD_RANGE_MIN = 0xf000;
const UTF8_3BYTE_LEAD_RANGE_MAX = 0xffff;
// readSync's final fallback (after its switch, before the default read_prn):
// a DBF version byte followed by a plausible last-update month (<=DBF_MONTH_MAX)
// and day (<=DBF_DAY_MAX) routes to DBF.to_workbook instead of the DSV/PRN
// parser — except version byte 0x03, which readSync's own inline switch
// returns on unconditionally, with no month/day check at all. Only the ASCII
// version bytes are listed here: the non-ASCII ones in xlsx.js's own
// DBF_SUPPORTED_VERSIONS (0x83, 0x8B, 0x8C, 0xF5) can never be the real lead
// byte, since bstrify (xlsx.js's own `type: 'string'` pre-pass) expands any
// character above 0x7F into its multi-byte UTF-8 sequence before dispatch —
// a decoded JS string whose first character happens to carry one of those
// code points dispatches on that sequence's own (different) lead byte.
const DBF_UNCONDITIONAL_VERSION = 0x03;
const DBF_CONDITIONAL_VERSIONS = new Set([0x02, 0x30, 0x31]); // 0x30/0x31 are the ASCII digits '0'/'1'
const DBF_MONTH_MAX = 12;
const DBF_DAY_MAX = 31;

/** The leading `count` bytes of `text` as bstrify would see them: ASCII
 *  unchanged, any other character expanded to its UTF-8 byte sequence.
 *  Needed only for the DBF check below — every other signature in this
 *  module matches on the text itself (`startsWith`/a single leading
 *  character), which doesn't depend on byte alignment, but the DBF check
 *  reads FIXED byte offsets (2 and 3) that an earlier non-ASCII character
 *  would shift out of sync with this module's own (un-bstrified) string
 *  positions. */
function leadBytesAfterBstrify(text: string, count: number): number[] {
    return Array.from(new TextEncoder().encode(text.slice(0, count))).slice(0, count);
}

/** True when SheetJS's DBF version-byte + plausible-month/day check would
 *  route `text` to DBF.to_workbook instead of the DSV/PRN parser. */
function isDbfRoute(text: string): boolean {
    const [lead, , month, day] = leadBytesAfterBstrify(text, 4);
    if (lead === DBF_UNCONDITIONAL_VERSION) return true;
    return DBF_CONDITIONAL_VERSIONS.has(lead) && month <= DBF_MONTH_MAX && day <= DBF_DAY_MAX;
}

/** readSync case 0x3C routes straight to its XML/HTML parser; case
 *  0x0A/0x0D/0x20 routes to read_plaintext_raw -> read_plaintext, which skips
 *  any run of '\r'/'\n'/' ' and THEN checks for '<' — anything else
 *  (including running out of text) falls through to the DSV parser on the
 *  text UNCHANGED (read_plaintext passes the ORIGINAL string, not the
 *  whitespace-stripped slice, on to its DSV/PRN parser). */
function leadsToXmlOrHtml(text: string): boolean {
    let i = 0;
    while (i < text.length && PLAINTEXT_SKIPPABLE_CHAR_CODES.has(text.charCodeAt(i))) i += 1;
    return text.charCodeAt(i) === XML_OR_HTML_LEAD_CHAR_CODE;
}

// SYLK record types (`sylk_to_aoa_str`, xlsx.js 0.18.5) and the per-field
// prefix characters its 'C' (cell) and 'F' (format/column/row) records
// accept. `read_wb_ID` always runs SYLK.to_workbook with WTF forced true
// internally, so ANY record type, field prefix, or the control-flow bugs
// below throws, and read_wb_ID silently falls back to PRN.to_workbook —
// which (since converters.ts never sets opts.PRN) is exactly
// dsv_to_sheet_str, i.e. this file's own DSV model. So "ID"-led text is
// genuine SYLK, and only advisory here, when ALL of the below hold for every
// record; anything else is a normal DSV file and gets the exact DSV estimate
// like any other CSV.
const SYLK_RECORD_TYPES = new Set(['ID', 'E', 'B', 'O', 'W', 'P', 'C', 'F']);
const SYLK_C_FIELD_CHARS = new Set(['A', 'X', 'Y', 'K', 'E', 'S', 'G', 'R', 'C']);
const SYLK_F_FIELD_CHARS = new Set(['X', 'Y', 'M', 'F', 'G', 'P', 'S', 'D', 'N', 'W', 'C', 'R']);
const NBSP = ' ';

/** F and C records both treat their own 'Y' field identically: move the
 *  shared row cursor and extend the allocated row range, so a later C
 *  record's K/E/S can rely on either having allocated it. */
function applySylkYField(fieldValue: string, allocatedRows: number): { row: number; allocatedRows: number } {
    const value = parseInt(fieldValue, 10);
    const row = Number.isNaN(value) ? NaN : value - 1;
    return { row, allocatedRows: Number.isInteger(row) && row + 1 > allocatedRows ? row + 1 : allocatedRows };
}

/** Lazy equivalent of `text.split(/[\n\r]+/)` (same empty first/last segments),
 *  so a caller that bails on the first line never materialises the rest. */
export function* sylkLines(text: string): Generator<string> {
    const separator = /[\n\r]+/g;
    let start = 0;
    let match = separator.exec(text);
    while (match !== null) {
        yield text.slice(start, match.index);
        start = separator.lastIndex;
        match = separator.exec(text);
    }
    yield text.slice(start);
}

/**
 * True when `text` parses as genuine SYLK (see the constants above for which
 * throw conditions are modelled: unknown record types; unknown 'C'/'F' field
 * prefixes; a bare "P" record, which dereferences its first field
 * unconditionally; a 'C' record's K/E/S field touching a row no 'Y' field (in
 * either a 'C' or an 'F' record) has yet allocated — including an 'F'
 * record's own 'R' (row-properties) field, which moves that same shared row
 * cursor WITHOUT allocating it; an 'F' record referencing a cell format by
 * index (its 'P' field), whose resolution — and the real parser's own throw
 * when a later numeric 'K' value hits an undefined format slot — isn't
 * modelled further, conservatively treated as not-genuine; a line that is
 * only an NBSP character, which survives THIS module's trim as blank but the
 * real (bstrified) parser sees as a non-blank "bad record"; and an 'S'
 * (shared formula) field, whose own base-cell resolution also isn't modelled
 * further, conservatively treated as not-genuine. Every conservative "not
 * genuine" call here only ever routes MORE text to the exact DSV estimate,
 * never fewer. Verified against the real parser over the fixtures and fuzz
 * corpus in sheetSizeGuard.test.ts — zero routing mismatches found there, not
 * a formal proof over every possible SYLK-shaped string.
 */
export function isGenuineSylk(text: string, lines: Iterable<string> = sylkLines(text)): boolean {
    let allocatedRows = 0;
    let row = -1;
    for (const line of lines) {
        const rstr = line.trim();
        if (!rstr) {
            // bstrify expands an NBSP into a 2-byte UTF-8 sequence whose lead
            // byte isn't whitespace — a line that looks blank here survives
            // trimming in the real parser as a non-empty "bad record".
            if (line.includes(NBSP)) return false;
            continue;
        }
        const fields = rstr
            .replace(/;;/g, '\u0000')
            .split(';')
            .map((f) => f.replace(/\u0000/g, ';'));
        const recordType = fields[0];
        if (!SYLK_RECORD_TYPES.has(recordType)) return false;
        if (recordType === 'P') {
            if (fields.length < 2) return false; // `record[1].charAt(0)` dereferenced unconditionally
            continue;
        }
        if (recordType === 'F') {
            let referencesFormat = false;
            for (let i = 1; i < fields.length; i += 1) {
                const tag = fields[i].charAt(0);
                if (!SYLK_F_FIELD_CHARS.has(tag)) return false;
                if (tag === 'Y') ({ row, allocatedRows } = applySylkYField(fields[i].slice(1), allocatedRows));
                else if (tag === 'R') {
                    const value = parseInt(fields[i].slice(1), 10);
                    row = Number.isNaN(value) ? NaN : value - 1; // row-properties: moves the cursor WITHOUT allocating it
                } else if (tag === 'P') referencesFormat = true;
            }
            if (referencesFormat) return false;
            continue;
        }
        if (recordType !== 'C') continue; // ID/E/B/O/W: no sub-field validation at all
        let sawK = false;
        let sawE = false;
        let sawS = false;
        for (let i = 1; i < fields.length; i += 1) {
            const tag = fields[i].charAt(0);
            if (!SYLK_C_FIELD_CHARS.has(tag)) return false;
            const rowIsAllocated = Number.isInteger(row) && row >= 0 && row < allocatedRows;
            if (tag === 'Y') ({ row, allocatedRows } = applySylkYField(fields[i].slice(1), allocatedRows));
            else if (tag === 'K') sawK = true; // the actual cell write happens after the loop; checked below
            else if (tag === 'E') {
                sawE = true;
                if (!rowIsAllocated) return false;
            } else if (tag === 'S') {
                sawS = true;
                if (!rowIsAllocated) return false;
            }
        }
        if (sawE && sawS) return false; // "SYLK shared formula cannot have own formula"
        if (sawS) return false; // shared-formula base resolution not modelled — conservative DSV fallback
        if (sawK && !(Number.isInteger(row) && row >= 0 && row < allocatedRows)) return false;
    }
    return true;
}

/**
 * True when SheetJS would route `text` to something other than its DSV/PRN
 * parser. Binary-only signatures in the same dispatch (the CFB/zip/Lotus
 * WK_/PNG/TIFF cases) need non-printable leading bytes that text already
 * decoded by `decodeText` for a CSV upload essentially never produces at
 * those positions, so they're not mirrored here; DBF's version byte and the
 * rare U+F000–U+FFFF-then-'<' lead ARE mirrored below since their trigger
 * byte values can survive decoding.
 */
function isNonDsvCsvRoute(text: string): boolean {
    const lead = text.charCodeAt(0);
    if (text.startsWith(DIF_HEADER)) return true;
    if (text.startsWith(SYLK_HEADER)) return isGenuineSylk(text);
    if (text.startsWith(RTF_HEADER)) return true;
    if (lead === BIFF_RAW_LEAD_CHAR_CODE && text.charCodeAt(1) <= BIFF_RAW_SECOND_CHAR_CODE_MAX) return true;
    if (lead >= UTF8_3BYTE_LEAD_RANGE_MIN && lead <= UTF8_3BYTE_LEAD_RANGE_MAX && text.charAt(1) === '<') return true;
    if (isDbfRoute(text)) return true;
    return leadsToXmlOrHtml(text);
}

/** A field is empty exactly when `dsv_to_sheet_str` (xlsx.js 0.18.5) would
 *  give it `cell.t = 'z'` and skip writing it — a simple `""` quoted field
 *  included (the common "export tool writes an explicit empty string" shape),
 *  and a lone `"` too (its own quote char doubles as both the opening and
 *  closing quote, so it unquotes to empty the same way). The one remaining
 *  gap: a row whose fields are ALL empty never writes a cell at all, so the
 *  real count is 0 — but the estimate's row/column extent still starts at 1x1,
 *  giving 1 instead of 0. Harmless (an over-count by exactly one, on an
 *  already-trivial file). */
function isDsvFieldEmpty(raw: string): boolean {
    const unquoted = raw.charAt(0) === '"' && raw.charAt(raw.length - 1) === '"' ? raw.slice(1, -1) : raw;
    return unquoted.length === 0;
}

/**
 * Direct port of `dsv_to_sheet_str`'s record/field-splitting loop (xlsx.js
 * 0.18.5), adapted to track only the dense-grid VALUE extent (the max
 * row/col index among non-empty fields) instead of building real cell
 * objects — matching the A1-anchored value extent (an upper bound on filled cells), the metric
 * `classifySize` is limited against, rather than the raw field extent (which
 * includes trailing empty fields a real parse never writes a cell for).
 * `fieldStartCharCode` mirrors xlsx.js's own `startcc`: captured ONCE per
 * field and never recomputed mid-field, it is the ONLY thing a `"` is
 * checked against to decide whether it opens/continues quoted mode — so a
 * `"` anywhere else in the field (a stray quote) is a no-op literal, exactly
 * like SheetJS's own scan, and NOT "is the scan currently inside quotes" (a
 * subtly different, and wrong, model). A CRLF is handled inline (skip the
 * `\r`, break on the following `\n`) rather than via an upfront
 * `text.replace(/\r\n/g, '\n')` copy, so a large file is scanned without
 * allocating a second full-length string.
 */
function csvDenseGridExtent(text: string, sep: string): { rows: number; cols: number; filled: number } {
    const sepCharCode = sep.charCodeAt(0);
    let filled = 0;
    let row = 0;
    let col = 0;
    let maxRow = 0;
    let maxCol = 0;
    let start = 0;
    let end = 0;
    let inQuotedField = false;
    let charCode = 0;
    let fieldStartCharCode = text.charCodeAt(0);

    const finishField = (fieldEnd: number): void => {
        if (!isDsvFieldEmpty(text.slice(start, fieldEnd))) {
            filled += 1;
            if (maxCol < col) maxCol = col;
            if (maxRow < row) maxRow = row;
        }
        if (charCode === sepCharCode) col += 1;
        else {
            col = 0;
            row += 1;
        }
        start = end + 1;
        fieldStartCharCode = text.charCodeAt(start);
    };

    for (; end < text.length; end += 1) {
        charCode = text.charCodeAt(end);
        if (charCode === QUOTE_CHAR_CODE) {
            if (fieldStartCharCode === QUOTE_CHAR_CODE) inQuotedField = !inQuotedField;
        } else if (!inQuotedField && charCode === CR_CHAR_CODE && text.charCodeAt(end + 1) === LF_CHAR_CODE) {
            // Part of a CRLF pair — the break fires on the LF below (one
            // break for the pair); a lone CR, handled in the branch below,
            // still fires immediately (legacy Mac line endings).
        } else if (!inQuotedField && (charCode === sepCharCode || charCode === LF_CHAR_CODE || charCode === CR_CHAR_CODE)) {
            // A LF that completed a CRLF pair must not pull the preceding
            // CR into the field's own content.
            const fieldEnd = charCode === LF_CHAR_CODE && text.charCodeAt(end - 1) === CR_CHAR_CODE ? end - 1 : end;
            finishField(fieldEnd);
        }
    }
    if (end - start > 0) finishField(end);

    return { rows: maxRow + 1, cols: maxCol + 1, filled };
}

/**
 * Counts the cells the CSV `dsv_to_sheet_str` (xlsx.js 0.18.5) would parse
 * `text` to — its non-empty fields, the filled cells `classifySize` is limited
 * against — by running the same separator resolution (a `sep=X` header,
 * stripped before the scan like the real parser strips it, else
 * `guessSeparator`'s port of `guess_sep`) and the same field-splitting
 * (`csvDenseGridExtent`). Not the rows x columns extent: a sparse export
 * (empty columns, blank fields) holds far fewer cells than its extent, and the
 * padded grid is limited separately after the parse. For any text
 * `isNonDsvCsvRoute` says SheetJS would also route to its DSV parser, this is
 * an exact match.
 */
export function estimateCsvCells(text: string): number {
    if (!text) return 0;
    const { sep, body } = resolveSepAndBody(text);
    return csvDenseGridExtent(body, sep).filled;
}

// JSZip's public typings don't declare either of these — `_data` (populated
// by loadAsync, holds the central-directory metadata) or `internalStream`
// (lets a part be read incrementally without decompressing the whole thing).
// Both are stable, long-standing internals of the 3.x line (narrowing cast,
// pattern: fortuneSheetSetup.ts's locale narrowing).
type JSZipInternal = { _data?: { uncompressedSize?: number } };
type JSZipStream = { on: (event: string, cb: (chunk?: string) => void) => JSZipStream; resume: () => void; pause: () => void };
type JSZipObjectWithInternals = JSZip.JSZipObject & JSZipInternal & { internalStream: (type: 'text') => JSZipStream };

/** Reads only as much of a sheet part's XML as needed to find `<dimension>`,
 *  capped at DIMENSION_HEAD_READ_BYTES — never decompresses the whole part. */
function readDimensionRef(part: JSZipObjectWithInternals): Promise<string | null> {
    return new Promise((resolve) => {
        let text = '';
        let settled = false;
        const stream = part.internalStream('text');
        const settle = (value: string | null) => {
            if (settled) return;
            settled = true;
            stream.pause();
            resolve(value);
        };
        stream.on('data', (chunk) => {
            text += chunk ?? '';
            const match = text.match(DIMENSION_RE);
            if (match) {
                settle(match[1]);
                return;
            }
            if (text.length >= DIMENSION_HEAD_READ_BYTES) settle(null);
        });
        stream.on('end', () => settle(null));
        stream.on('error', () => settle(null));
        stream.resume();
    });
}

/** Streams a sheet part counting its `<c>` cell elements, stopping once `limit`
 *  is reached — O(bytes) with bounded memory, never the whole part in one
 *  string. Resolves null on a stream error, so the caller keeps the byte bound. */
function countCellTags(part: JSZipObjectWithInternals, limit: number): Promise<number | null> {
    return new Promise((resolve) => {
        let count = 0;
        let carry = '';
        let settled = false;
        const stream = part.internalStream('text');
        const settle = (value: number | null) => {
            if (settled) return;
            settled = true;
            stream.pause();
            resolve(value);
        };
        stream.on('data', (chunk) => {
            const text = carry + (chunk ?? '');
            CELL_TAG_RE.lastIndex = 0;
            for (let match = CELL_TAG_RE.exec(text); match; match = CELL_TAG_RE.exec(text)) {
                // A match wholly inside the carry was already counted with the previous chunk.
                if (match.index + match[0].length > carry.length) count += 1;
            }
            carry = text.slice(-CELL_TAG_CARRY_CHARS);
            if (count >= limit) settle(count);
        });
        stream.on('end', () => settle(count));
        stream.on('error', () => settle(null));
        stream.resume();
    });
}

async function estimateXlsxCells(buffer: ArrayBuffer): Promise<number> {
    const zip = await JSZip.loadAsync(buffer);
    const parts = zip.file(SHEET_XML_PATTERN) as JSZipObjectWithInternals[];
    const perSheet: SheetPartEstimate[] = await Promise.all(
        parts.map(async (part) => ({
            dimRef: await readDimensionRef(part),
            xmlBytes: part._data?.uncompressedSize ?? 0,
        })),
    );
    const estimate = estimateFromParts(perSheet);
    if (estimate <= MAX_OPENABLE_CELLS) return estimate;
    // Both bounds can over-count far past the real cells (streaming writers omit
    // <dimension>; LibreOffice declares 1,024 columns over formatting-only cells; a
    // declared range can be sparsely filled), so before refusing, every part's cell
    // tags are counted — against one shared budget, so a genuinely huge file is
    // still refused, and a refusal keeps today's number.
    let budget = MAX_OPENABLE_CELLS + 1;
    for (let i = 0; i < perSheet.length && budget > 0; i += 1) {
        const cellTags = await countCellTags(parts[i], budget);
        if (cellTags == null) continue;
        perSheet[i].cellTags = cellTags;
        budget -= cellTags;
    }
    const refined = budget > 0 ? estimateFromParts(perSheet) : estimate;
    return refined > MAX_OPENABLE_CELLS ? estimate : refined;
}

export interface CellEstimate {
    cells: number;
    /** False for two advisory-only estimates that must never refuse a file
     *  outright: the legacy xls/BIFF estimate (`BIFF_BYTES_PER_CELL` is a
     *  measured AVERAGE, not a genuine upper bound — a byte-heavy but
     *  cell-sparse .xls, e.g. mostly embedded images, can wildly
     *  overestimate), and CSV text SheetJS routes to a non-DSV parser (see
     *  `isNonDsvCsvRoute` — genuine SYLK, DIF, RTF, or a SpreadsheetML/HTML
     *  table, none of which `estimateCsvCells`'s DSV model has any bearing
     *  on). True for xlsx's `<dimension>`/XML-bytes estimate, and for any CSV
     *  text `estimateCsvCells` scans (an upper bound, exact wherever
     *  `estimateCsvCells`'s own doc comment says so). `classifySize` caps a
     *  non-upper-bound estimate at 'large' instead of 'too_large'; the
     *  post-parse dense-grid guard (denseGridCounts/isDenseGridTooLarge) is
     *  the deciding too-large check for it instead. */
    isUpperBound: boolean;
}

/** Rough byte density for CSV-labelled text SheetJS routes to a non-DSV
 *  parser (SpreadsheetML/HTML table, SYLK, DIF, RTF — see `isNonDsvCsvRoute`)
 *  instead of the DSV one `estimateCsvCells` models — not a measured average
 *  like BIFF_BYTES_PER_CELL, just small enough that a real table's worth of
 *  `<td>`/`<Cell>` markup divides out to more cells than bytes alone would
 *  suggest. Not a genuine upper bound, same caveat as BIFF_BYTES_PER_CELL —
 *  see CellEstimate's own doc comment. */
const NON_DSV_TEXT_BYTES_PER_CELL = 10;

/**
 * Estimate the number of cells a workbook will parse to, without running the
 * real parser. Dispatch mirrors `converters.fileBytesToFortuneSheets`
 * (xlsx-by-content, then csv-by-mime/extension), with a third branch for
 * legacy xls/BIFF (not a zip, no central directory).
 */
export async function estimateCells(buffer: ArrayBuffer, mimeType?: string, fileName?: string): Promise<CellEstimate> {
    if (isXlsxBuffer(buffer)) return { cells: await estimateXlsxCells(buffer), isUpperBound: true };
    const ext = fileName?.split('.').pop()?.toLowerCase();
    if (mimeType === 'text/csv' || mimeType === 'application/csv' || ext === 'csv') {
        const text = decodeText(buffer);
        // SheetJS's own dispatch (see isNonDsvCsvRoute) routes some CSV-
        // labelled text to a parser estimateCsvCells's DSV model has no
        // bearing on (e.g. a `<table>` full of `<td>` cells can run to many
        // more cells than its separator/line-break count alone would
        // suggest), so it isn't a genuine upper bound here.
        if (isNonDsvCsvRoute(text)) return { cells: buffer.byteLength / NON_DSV_TEXT_BYTES_PER_CELL, isUpperBound: false };
        return { cells: estimateCsvCells(text), isUpperBound: true };
    }
    return { cells: buffer.byteLength / BIFF_BYTES_PER_CELL, isUpperBound: false };
}

/** `isUpperBound` (default true) caps the result at 'large' instead of
 *  'too_large' for an estimate that's only advisory (xls, or CSV text routed
 *  away from the DSV parser) — see CellEstimate's own doc comment. */
export function classifySize(cells: number, isUpperBound = true): SheetSizeClass {
    if (cells > MAX_OPENABLE_CELLS) return isUpperBound ? 'too_large' : 'large';
    if (cells > LARGE_NOTICE_CELLS) return 'large';
    return 'ok';
}

export interface SheetSizeCheck {
    cells: number;
    classification: SheetSizeClass;
}

/** Combines `estimateCells` + `classifySize` — the single entry point the
 *  worker (and the perf bench script) call. Never rejects: a magic-bytes-
 *  valid but corrupt zip (JSZip.loadAsync) — or any other estimate failure —
 *  must not itself block an upload or trip a caller with no catch; the real
 *  parse reads the SAME bytes and is the authoritative check, so an estimate
 *  failure just defers to it via the 'unknown' classification (never
 *  'too_large' — this guard must never be the reason a genuinely openable
 *  file gets refused). */
export async function checkSheetSize(buffer: ArrayBuffer, mimeType?: string, fileName?: string): Promise<SheetSizeCheck> {
    try {
        const { cells, isUpperBound } = await estimateCells(buffer, mimeType, fileName);
        return { cells, classification: classifySize(cells, isUpperBound) };
    } catch (err) {
        console.warn('[sheetSizeGuard] checkSheetSize estimate failed, deferring to the real parse', err);
        return { cells: 0, classification: 'unknown' };
    }
}

/** Padded dense-matrix allocation ceiling (memory refusal only), independent of
 *  the filled-cell caps. FortuneSheet pads every sheet to MIN_ROWS x MIN_COLS,
 *  so a narrow-but-tall sheet allocates far more slots than it has cells. Sized
 *  to cover 1,048,576 rows x the 26-column floor (27,262,976). */
export const MAX_OPENABLE_GRID_SLOTS = 30_000_000;

export interface DenseGridCounts {
    /** Σ celldata.length per sheet — cells holding a value or formula. The
     *  user-facing count (copy, telemetry `cellCount`) and the edit/refuse
     *  decision. */
    filledCells: number;
    /** Σ max(MIN_ROWS,maxR+1) x max(MIN_COLS,maxC+1) per sheet — the actual
     *  padded dense-matrix allocation Fortune-Sheet makes. Internal only. */
    gridSlots: number;
}

/**
 * Post-convert guard: one pass over each sheet's celldata (0 for an empty
 * sheet) computes the filled-cell count and the padded dense-matrix
 * allocation (floored to MIN_ROWS x MIN_COLS, as converters.ts pads every
 * sheet). Re-checked right after conversion, before the sheets are posted back.
 */
export function denseGridCounts(sheets: FortuneSheet[]): DenseGridCounts {
    return sheets.reduce<DenseGridCounts>(
        (totals, sheet) => {
            const celldata = sheet.celldata ?? [];
            if (!celldata.length) return totals;
            let maxR = 0;
            let maxC = 0;
            for (const cell of celldata) {
                if (cell.r > maxR) maxR = cell.r;
                if (cell.c > maxC) maxC = cell.c;
            }
            return {
                filledCells: totals.filledCells + celldata.length,
                // The declared row/column count (the SheetJS/.xls/.csv path's
                // own `!ref` extent) can exceed the cell extent above — a
                // sparse sheet with a huge declared range still allocates a
                // matrix that large, so the ceiling must take the max of both.
                gridSlots:
                    totals.gridSlots + Math.max(MIN_ROWS, maxR + 1, sheet.row ?? 0) * Math.max(MIN_COLS, maxC + 1, sheet.column ?? 0),
            };
        },
        { filledCells: 0, gridSlots: 0 },
    );
}

/** The grid the editor actually MOUNTS: useLazySheets parks a stray far tail
 *  outside the grid (splitSparseTail), so refusing on the untrimmed grid would
 *  refuse a sheet the user never gets. filledCells stays untrimmed — the tail
 *  is saved back, so its cells still count. Trimming only ever shrinks the
 *  grid, so it is computed only when the untrimmed grid is over the cap. */
export function mountedGridCounts(sheets: FortuneSheet[]): DenseGridCounts {
    const counts = denseGridCounts(sheets);
    if (counts.gridSlots <= MAX_OPENABLE_GRID_SLOTS) return counts;
    return { filledCells: counts.filledCells, gridSlots: denseGridCounts(sheets.map((sheet) => splitSparseTail(sheet).sheet)).gridSlots };
}

/** True when either refusal limit is exceeded — the single post-convert
 *  too-large check the worker and the main-thread guarded path both share. */
export function isDenseGridTooLarge(counts: DenseGridCounts): boolean {
    return counts.filledCells > MAX_OPENABLE_CELLS || counts.gridSlots > MAX_OPENABLE_GRID_SLOTS;
}

/** Which count to report for a refused workbook: filled cells when they are
 *  over the ceiling, else the padded grid. */
export function tooLargeBasis(counts: DenseGridCounts): { cells: number; basis: TooLargeBasis } {
    return counts.filledCells > MAX_OPENABLE_CELLS
        ? { cells: counts.filledCells, basis: 'filled' }
        : { cells: counts.gridSlots, basis: 'grid' };
}

/** Filled cells across the sheets — Σ celldata.length, O(sheets), walks no cells. */
export function countFilledCells(sheets: FortuneSheet[]): number {
    return sheets.reduce((total, sheet) => total + (sheet.celldata?.length ?? 0), 0);
}

/** Narrows an Add-to-Workspace argument to a real band: a host's `onAddToReports`
 *  can also be wired to a plain click handler, which would pass a MouseEvent. */
export const isReadOnlyAddBand = (value: unknown): value is ReadOnlyAddBand =>
    value instanceof Object &&
    (value as ReadOnlyAddBand).rawBytes instanceof ArrayBuffer &&
    typeof (value as ReadOnlyAddBand).filledCells === 'number';

/** True when an openable workbook has too many filled cells to edit. */
export const isReadOnlySize = (filledCells: number): boolean => filledCells > MAX_EDITABLE_CELLS;

const formatCellCount = (cells: number): string => Math.round(cells).toLocaleString('en-US');

const DOWNLOAD_TO_EXCEL = 'Download it to open in Excel.';

const tooLargeMessage = (cells: number, basis: TooLargeBasis, verb: 'open' | 'preview'): string => {
    if (basis === 'grid')
        return `This file is too large to ${verb} here (its sheets span ${formatCellCount(cells)} cells; the limit is ${formatCellCount(
            MAX_OPENABLE_GRID_SLOTS,
        )}). ${DOWNLOAD_TO_EXCEL}`;
    // A pre-parse estimate counts every cell the file stores, empty ones included
    // (formatting-only xlsx cells, empty CSV fields), not only cells with data.
    const count =
        basis === 'estimate' ? `about ${formatCellCount(cells)} cells, including empty ones` : `${formatCellCount(cells)} cells with data`;
    return `This file is too large to ${verb} here (${count}; the limit is ${formatCellCount(MAX_OPENABLE_CELLS)}). ${DOWNLOAD_TO_EXCEL}`;
};

export const tooLargeToOpenMessage = (cells: number, basis: TooLargeBasis = 'filled'): string => tooLargeMessage(cells, basis, 'open');

export const tooLargeToPreviewMessage = (cells: number, basis: TooLargeBasis = 'filled'): string =>
    tooLargeMessage(cells, basis, 'preview');

export const readOnlyNoticeMessage = (filledCells: number): string =>
    `Read-only: this file has ${formatCellCount(filledCells)} cells with data, over the ${formatCellCount(
        MAX_EDITABLE_CELLS,
    )} that can be edited here. Download it to edit in Excel.`;

const readOnlyXlsxOnlyMessage = (filledCells: number, action: 'added to' | 'saved to', followUp: 'add' | 'import'): string =>
    `This file has ${formatCellCount(filledCells)} cells with data. Files with more than ${formatCellCount(
        MAX_EDITABLE_CELLS,
    )} can be ${action} Workspace only as .xlsx. Save it as .xlsx in Excel, then ${followUp} it.`;

export const readOnlyAddMessage = (filledCells: number): string => readOnlyXlsxOnlyMessage(filledCells, 'added to', 'add');

export const readOnlySaveMessage = (filledCells: number): string => readOnlyXlsxOnlyMessage(filledCells, 'saved to', 'import');

export const READ_ONLY_SHORTCUT_SAVE_NOTICE = 'This file is read-only here. Download it to edit in Excel.';

export const READ_ONLY_HISTORY_UNAVAILABLE = "Version history isn't available for files this large.";

export const READ_ONLY_DOWNLOAD_UNAVAILABLE = "Download isn't available for this file.";

export const LARGE_FILE_NOTICE = 'Large file — may take a few seconds';

/** Used by the Reports import catch and any other surface that only wants a
 *  toast string: the too-large / read-only-add copy, else `fallback`. */
export function sheetLoadErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof SheetTooLargeError) return tooLargeToOpenMessage(err.cells, err.basis);
    if (err instanceof SheetReadOnlyAddError) return readOnlyAddMessage(err.cells);
    return fallback;
}
